import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Button,
  CircularProgress,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import default_theme from "assets/themes/themes";
import { fetchDynamoRequest } from "../../../redux/lap/lapSlice";

const DynamoDetailPage = () => {
  const { dynamoCode } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  const lapState = useSelector((state) => state.lap);
  const dynamo = dynamoCode ? lapState.dynamos[dynamoCode] : null;
  const loading = lapState.loading;

  useEffect(() => {
    if (dynamoCode) {
      dispatch(fetchDynamoRequest({ dynamoCode }));
    }
  }, [dynamoCode, dispatch]);

  const handleBackClick = () => {
    navigate("/solutions/rural-development/lap-catalog");
  };

  const handleCatalogClick = (catalog) => {
    // Include dynamoCode in the URL so the LAP detail page can navigate back to this dynamo.
    navigate(
      `/solutions/rural-development/lap-dynamo/${dynamoCode}/lap-catalog/${catalog.entryId}`
    );
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "50vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!dynamo || !dynamo.combinedData) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Typography variant="h5">Dynamo not found</Typography>
      </Container>
    );
  }

  const data = dynamo.combinedData;
  const catalogs = dynamo.catalogs || [];

  return (
    <Box
      sx={{
        flex: 1,
        backgroundColor: default_theme.palette.primary.white,
        minHeight: "100%",
        py: 4,
      }}
    >
      <Container maxWidth="lg">
        <Box
          sx={{
            mb: 3,
            display: "flex",
            alignItems: "center",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Button
            variant="text"
            startIcon={<ArrowBackIcon />}
            onClick={handleBackClick}
            sx={{ color: default_theme.palette.other.main }}
          >
            Back to Local Action Plans
          </Button>
        </Box>

        <Paper sx={{ p: 4, mb: 4 }}>
          <Typography variant="h4" sx={{ fontWeight: "bold", mb: 2 }}>
            {data.solution_title || data.title || dynamo.entryId}
          </Typography>

          {data.brief_description && (
            <Typography
              variant="body1"
              sx={{ whiteSpace: "pre-wrap", color: "text.secondary" }}
            >
              {data.brief_description}
            </Typography>
          )}
        </Paper>

        <Box sx={{ mb: 2 }}>
          <Typography variant="h5" sx={{ fontWeight: "bold", mb: 1 }}>
            Local Action Plan catalogs
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Select one of the catalogs below to view its full solution details.
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {catalogs.map((catalog) => (
            <Grid item xs={12} sm={6} md={4} key={catalog.entryId}>
              <Paper
                sx={{
                  p: 3,
                  cursor: "pointer",
                  height: 260,
                  display: "flex",
                  flexDirection: "column",
                  borderRadius: "12px",
                  border: "1px solid",
                  borderColor: "divider",
                  "&:hover": {
                    backgroundColor: default_theme.palette.other.backgroundSubtle,
                    boxShadow: default_theme.palette.other.cardShadow,
                  },
                  transition: "all 0.2s ease-in-out",
                }}
                onClick={() => handleCatalogClick(catalog)}
              >
                <Typography
                  variant="h6"
                  sx={{
                    mb: 2,
                    fontWeight: "bold",
                    display: "-webkit-box",
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }}
                >
                  {catalog.combinedData.solution_title ||
                    catalog.combinedData.title ||
                    "Catalog"}
                </Typography>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    flex: 1,
                    display: "-webkit-box",
                    WebkitLineClamp: 6,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }}
                >
                  {catalog.combinedData.brief_description ||
                    "Click to view catalog details"}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default DynamoDetailPage;


